import { useEffect, useRef, type RefObject } from 'react'

// Safety net for a hover preview whose pointerleave never arrives: the hovered
// button can unmount mid-hover (React fires no leave for removed nodes), and
// Safari doesn't synthesize boundary events when content scrolls out from
// under a stationary cursor. Either way hoveredPlan/hoveredBranchTargetId
// latch on and the HUD graph loops the hovered plan's autoplay sweep forever.
//
// While a plan is hovered, watch document-level mouse moves: as soon as the
// pointer is demonstrably outside the choice grid, clear the hover. Also clear
// on window blur (cmd-tabbing away can strand the pointer mid-hover with no
// further events). Mouse-only, matching the enter guard in
// playbookBranchOptionModel — touch never sets this hover state.
export const useStuckHoverSafetyNet = ({
  isHovering,
  gridRef,
  onClear,
}: {
  isHovering: boolean
  gridRef: RefObject<HTMLDivElement | null>
  onClear: () => void
}) => {
  // Ref'd so the listeners survive re-renders without re-attaching (the clear
  // callback is recreated every render upstream).
  const onClearRef = useRef(onClear)
  onClearRef.current = onClear

  useEffect(() => {
    if (!isHovering) return
    const handlePointerMove = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') return
      const grid = gridRef.current
      if (grid && event.target instanceof Node && grid.contains(event.target)) return
      onClearRef.current()
    }
    const handleWindowBlur = () => onClearRef.current()
    document.addEventListener('pointermove', handlePointerMove, { passive: true })
    window.addEventListener('blur', handleWindowBlur)
    return () => {
      document.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('blur', handleWindowBlur)
      // Also clear on teardown: if the whole choice UI unmounts mid-hover, no
      // leave will ever fire and this hook disappears with it — this is the
      // last chance to release the parent's hoveredPlan. Redundant clears
      // (normal hover-end re-runs this cleanup too) are no-op setState calls.
      onClearRef.current()
    }
  }, [isHovering, gridRef])
}
